import get from 'lodash/get';
import { Auth } from 'aws-amplify';
import EmailCaptureComponent from 'components/email-capture/js/email-capture.component';
import emailCapturingApi from 'general/js/api/email-capturing-api';
import HtmlHelper from 'foundation/html-helper';
import UrlHelper from 'foundation/url-helper';
import pageSpinner from '../page-spinner';
import api, { isHtmlContentType } from '../api';
import app from '../app';
import Modal, { bemClasses } from '../modal';
import * as cookieHelper from '../../../components/sso/helpers/cookie-helper';
import { streamConstants } from '../stream-constants';

const STREAM_COOKIE_EXPIRATION_TIME_IN_MINUTES = 10;
const COGNITO_SSO_MODE = 'Cognito';
const LOCAL_STORAGE_STATE_KEY = 'emailCaptureState';
const LOCAL_STORAGE_USERID_KEY = 'emailCaptureUserId';
const LOCAL_STORAGE_USER_DATA_KEY = 'emailCaptureUserData';
const COMPONENT_ELEMENT_ID = 'email-capture';

const DEFAULT_CAPTURE_STATE = {
    userId: null,
};

class EmailCapturingService {
    constructor() {
        this.cognitoModal = null;
        this.cognitoElement = null;
        this._componentPromise = null;
        this._elementPromise = null;
        this._storage = window.localStorage;

        const emailCapture = get(app.state, 'emailCapture', {});
        const ssoMode = get(app.state, 'ssoMode', {});
        this._ssoMode = ssoMode;
        this._capturePageUrl = emailCapture.capturePageUrl;
        this._captureUrl = emailCapture.captureUrl;
        this._checkEmailUrl = emailCapture.checkEmailUrl;
        this._trackUrl = emailCapture.trackUrl;
        this._captureTTLSeconds = emailCapture.captureTTLSeconds;
        this._trackTTLSeconds = emailCapture.trackTTLSeconds;
        this._temporaryAccessTTLSeconds = emailCapture.temporaryAccessTTLSeconds;

        this._state = this._getState();
    }

    /**
     * Please make sure condition strictly equals to challenge method logic
     * Otherwise there can be cases with out of sync behavior
     * @return {*}
     */
    isResolvedWithoutCapture() {
        return app.isEditMode ||
            this._isCaptured() ||
            (this._isTemporaryAccessEnabled() && (this._isTemporaryAccessValid() || this._isSilentCaptureAllowed()));
    }

    challenge(onResolve, videoId, isSynchronousResolveRequired = false) {
        this._handleCognitoChallenge(onResolve, videoId, isSynchronousResolveRequired);
        this._handleDefaultChallenge(onResolve, isSynchronousResolveRequired);
    }

    getEmailCaptureState() {
        return this._storage.getItem(LOCAL_STORAGE_STATE_KEY);
    }

    removeEmailCaptureState() {
        this._storage.removeItem(LOCAL_STORAGE_STATE_KEY);
    }

    _handleCognitoChallenge(onResolve, videoId, isSynchronousResolveRequired = false) {
        if (this._ssoMode !== COGNITO_SSO_MODE) {
            return;
        }

        Auth.currentAuthenticatedUser()
            .then((user) => {
                if (user) {
                    this._captureData(this._onResolve.bind(this, onResolve, isSynchronousResolveRequired));
                }
                cookieHelper.removeCookie(streamConstants.videoToBePlayedId);
            })
            .catch(() => {
                cookieHelper.setCookieWithExpirationDate(streamConstants.videoToBePlayedId, videoId, STREAM_COOKIE_EXPIRATION_TIME_IN_MINUTES, true);
                this._getCognitoComponent();
            });
    }

    _handleDefaultChallenge(onResolve, isSynchronousResolveRequired = false) {
        if (this._ssoMode === COGNITO_SSO_MODE) {
            return;
        }

        if (app.isEditMode) {
            onResolve();
        }

        if (this._isCaptured()) {
            onResolve();
            this._trackChallenge();
            return;
        }

        if (this._isTemporaryAccessEnabled()) {
            if (this._isTemporaryAccessValid()) {
                onResolve();
                return;
            }
            if (this._isSilentCaptureAllowed()) {
                //check if we already have previously saved user data to submit it silently
                onResolve();
                this._silentCapture();
                return;
            }
        }

        // otherwise show form
        this._captureData(this._onResolve.bind(this, onResolve, isSynchronousResolveRequired));
    }

    _onResolve(onResolve, isSynchronousResolveRequired, isAsync = false) {
        // for some components (like audio, because of window.open logic) it's crucial to pass a challenge synchronously
        // it means that there should be no delay between user click and actual onResolve call
        // the workaround for this is to determine such cases by isSynchronousResolveRequired parameter
        // and call _onResolve wrapper with isAsync flag
        if (isAsync && isSynchronousResolveRequired) {
            // in this case we ignore resolving and make user trigger challenge the second time
            return;
        }

        onResolve();
    }

    _isSilentCaptureAllowed() {
        return this._getStoredUserData() !== null;
    }

    _getStoredUserData() {
        let result = null;

        const userDataString = this._storage.getItem(LOCAL_STORAGE_USER_DATA_KEY);
        if (userDataString) {
            try {
                result = JSON.parse(userDataString);
            } catch (e) {
                this._logError(e);
            }
        }

        return result;
    }

    _handleUserIdReceive(response) {
        const id = get(response, 'data.id');
        if (id) {
            this._successUserIdReceived(id);
        } else {
            this._errorUserIdReceived(new Error('no id attribute within the server response'));
        }
    }

    _silentCapture() {
        const userData = this._getStoredUserData();
        emailCapturingApi.post(this._captureUrl, userData).then((response) => {
            this._handleUserIdReceive(response);
        }, (error) => {
            this._errorUserIdReceived(error);
        });
    }

    _getState() {
        // always reset to default value
        let result = DEFAULT_CAPTURE_STATE;
        try {
            const storedString = this._storage.getItem(LOCAL_STORAGE_STATE_KEY);
            if (storedString !== null) {
                result = JSON.parse(storedString);
            }
        } catch (e) {
            this._logError(e);
        }

        return result;
    }

    _logError(error) {
        console.error(error);
        if (window.newrelic && window.newrelic.noticeError && typeof window.newrelic.noticeError === 'function') {
            try {
                throw error;
            } catch (err) {
                window.newrelic.noticeError(err);
            }
        }
    }

    _handleCaptureProcessError(error) {
        this._logError(error);
        this._setTemporaryAccess();
    }

    _captureData(onResolve) {
        try {
            // before capturing data we need to make sure there is no data stored
            this._resetStoredData();
            this._getComponent().then((component) => {
                component.capture(
                    this._successUserDataSent.bind(this, onResolve),
                    this._successUserCaptured,
                    this._errorUserCaptured,
                    this._successEmailChecked.bind(this, onResolve),
                    this._errorEmailChecked.bind(this, onResolve)
                );
            }).catch((error) => {
                onResolve();
                this._handleCaptureProcessError(error);
            });
        } catch (error) {
            onResolve();
            this._handleCaptureProcessError(error);
        }
    }

    _successUserDataSent = (onResolve, userData) => {
        onResolve();
        // user has submitted his information but the result isn't clear yet
        // we need to allow user to see restricted content
        // and react on the submission's result later
        // in case something goes wrong we have to save user's data
        // and try to submit it later again
        this._saveUserData(userData);
        // set temp access for user in order not to show challenges again during the email capture request
        this._setTemporaryAccess();
    };

    _successUserCaptured = (response) => {
        this._handleUserIdReceive(response);
    }

    _errorUserCaptured = (error) => {
        this._errorUserIdReceived(error);
    }

    _successEmailChecked = (onResolve, response) => {
        onResolve(true);
        this._handleUserIdReceive(response);
    }

    _errorEmailChecked = (onResolve, error) => {
        onResolve(true);
        this._errorUserIdReceived(error);
    }

    _saveUserData(userData) {
        this._storage.setItem(LOCAL_STORAGE_USER_DATA_KEY, JSON.stringify(userData));
    }

    _removeUserData() {
        this._storage.removeItem(LOCAL_STORAGE_USER_DATA_KEY);
    }

    _resetStoredData() {
        this._removeUserData();
    }

    _successUserIdReceived = (userId) => {
        this._saveSuccessCaptureInfo(userId);
    }

    _errorUserIdReceived = (error) => {
        // in case of network request error or server unavailability
        // we need to mark current process as not finished
        // and send userData again later
        this._logError(error);
        this._setTemporaryAccess();
    }

    _isCaptured() {
        return this._getUserId() && this._isCapturingValid();
    }

    _getUserId() {
        return this._state.userId;
    }

    _getCapturedTimestamp() {
        return this._state.timestamp;
    }

    _getTemporaryAccessTimestamp() {
        return this._state.temporaryAccessTimestamp;
    }

    _getTrackingTimestamp() {
        return this._state.trackingTimestamp;
    }

    _isTemporaryAccessEnabled() {
        return !!this._getTemporaryAccessTimestamp();
    }

    _isTemporaryAccessValid() {
        const temporaryAccessTimestamp = this._getTemporaryAccessTimestamp();
        return temporaryAccessTimestamp && (Date.now() < temporaryAccessTimestamp + this._temporaryAccessTTLSeconds * 1000);
    }

    _isCapturingTrackingExpired() {
        const currentTimestamp = Date.now();
        return currentTimestamp > this._getTrackingTimestamp() + this._trackTTLSeconds * 1000;
    }

    _trackChallenge() {
        if (this._isCapturingTrackingExpired()) {
            emailCapturingApi.post(this._trackUrl, { id: this._getUserId() }).then(() => {
                console.log('Captured email has been successfully tracked');
                this._updateTrackingTimestamp();
            }, (error) => {
                this._logError(error);
                this._updateTrackingTimestamp();
            });
        }
    }

    _isCapturingValid() {
        const timestamp = this._getCapturedTimestamp();
        const currentTimestamp = Date.now();
        return timestamp && (currentTimestamp < timestamp + this._captureTTLSeconds * 1000);
    }

    _getCognitoComponent() {
        if (this.cognitoElement) {
            this.cognitoModal.open();
            return;
        }

        pageSpinner.show();
        api.get(this._capturePageUrl)
            .then((response) => {
                pageSpinner.hide();
                if (isHtmlContentType(response)) {
                    this.cognitoElement = response.data;
                    this.cognitoModal = new Modal(response.data, {
                        bemList: [bemClasses.AUTO, bemClasses.SPLIT]
                    });
                    this.cognitoModal.open();
                }
            }).catch((error) => {
                console.log(error);
                pageSpinner.hide();
            });
    }

    _getComponent() {
        if (this._componentPromise === null) {
            this._componentPromise = this._getElement().then((element) => {
                return new EmailCaptureComponent(element, 'email-capture', {
                    captureUrl: this._captureUrl,
                    checkEmailUrl: this._checkEmailUrl
                });
            });
        }

        return this._componentPromise;
    }

    _getElement() {
        if (this._elementPromise === null) {
            pageSpinner.show();
            this._elementPromise = api.get(UrlHelper.getOverlayUrl(this._capturePageUrl)).then((response) => {
                if (isHtmlContentType(response)) {
                    const targetHtml = response.data;
                    const element = HtmlHelper.createElement(targetHtml);
                    document.body.appendChild(element);
                    return document.getElementById(COMPONENT_ELEMENT_ID);
                }

                return Promise.reject();
            }).then((result) => {
                pageSpinner.hide();
                return result;
            }, (error) => {
                pageSpinner.hide();
                return Promise.reject(error);
            });
        }
        return this._elementPromise;
    }

    _saveStateToStorage(value) {
        const storedValue = JSON.stringify(value);
        this._storage.setItem(LOCAL_STORAGE_STATE_KEY, storedValue);
    }

    _saveUserIdToStorage(userId) {
        if (userId) {
            this._storage.setItem(LOCAL_STORAGE_USERID_KEY, userId);
        } else {
            this._storage.removeItem(LOCAL_STORAGE_USERID_KEY);
        }
    }

    _saveSuccessCaptureInfo(userId) {
        const currentTimestamp = Date.now();
        const captureInfo = {
            userId,
            timestamp: currentTimestamp,
            trackingTimestamp: currentTimestamp
        };
        this._saveStateToStorage(captureInfo);
        this._saveUserIdToStorage(userId);
        this._state = captureInfo;
    }

    _updateTrackingTimestamp() {
        const newCapturedState = { ...this._state, trackingTimestamp: Date.now() };
        this._saveStateToStorage(newCapturedState);
        this._state = newCapturedState;
    }

    _setTemporaryAccess() {
        const userId = null;

        const state = {
            userId,
            temporaryAccessTimestamp: Date.now()
        };
        this._saveStateToStorage(state);
        this._saveUserIdToStorage(userId);
        this._state = state;
    }
}

const instance = new EmailCapturingService();
export default instance;
